import React, { useState, useEffect, useContext, createContext } from 'react';
import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    getDoc,
    setDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    writeBatch,
    serverTimestamp
} from "firebase/firestore";
import { db, appId } from '../config/firebase';
import { useAuth } from './AuthContext';

// --- [START] Siswa Data Hub Context ---
const SiswaContext = createContext();

export const SiswaProvider = ({ children }) => {
    const { user } = useAuth(); // Get user from Auth context
    const [students, setStudents] = useState([]);
    const [assessmentTypes, setAssessmentTypes] = useState([]);
    const [progress, setProgress] = useState({});
    const [cptps, setCptps] = useState([]);

    // --- Firestore Collection References ---
    const getCollectionRef = (collectionName) => {
        if (!user) return null;

        // Untuk collection 'siswa', gunakan collection global
        if (collectionName === 'siswa') {
            return collection(db, `artifacts/${appId}/siswa`);
        }

        // Untuk collection lain, gunakan user-specific
        return collection(db, `artifacts/${appId}/users/${user.uid}/${collectionName}`);
    };

    // 🔧 PERBAIKAN: Fungsi untuk mendapatkan referensi koleksi siswa berdasarkan sekolah
    const getSiswaCollectionRef = (schoolId = null) => {
        if (!user) return null;

        // Jika tidak ada schoolId, gunakan collection global (untuk kompatibilitas)
        if (!schoolId) {
            return collection(db, `artifacts/${appId}/siswa`);
        }

        // Jika ada schoolId, gunakan path yang terpisah berdasarkan sekolah
        return collection(db, `artifacts/${appId}/schools/${schoolId}/siswa`);
    };

    // 🔧 PERBAIKAN: Fungsi untuk mendapatkan referensi koleksi siswa berdasarkan nama sekolah
    const getSiswaCollectionRefBySchoolName = (namaSekolah = null) => {
        if (!user || !namaSekolah) {
            return collection(db, `artifacts/${appId}/siswa`);
        }

        // Gunakan nama sekolah sebagai identifier untuk path
        const schoolPath = namaSekolah.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
        return collection(db, `artifacts/${appId}/schools/${schoolPath}/siswa`);
    };

    // --- Data Loading Effect ---
    useEffect(() => {
        if (!user) {
            setStudents([]);
            setAssessmentTypes([]);
            setProgress({});
            return;
        }

        // Function to initialize data for new users
        const initializeData = async (collectionRef, initialData) => {
            const snapshot = await getDocs(collectionRef);
            if (snapshot.empty) {
                const batch = writeBatch(db);
                initialData.forEach(item => {
                    const docRef = doc(collectionRef); // Auto-generate ID
                    batch.set(docRef, { ...item, createdAt: serverTimestamp() });
                });
                await batch.commit();
            }
        };

        const initialAssessmentTypes = [
            { name: 'Ulangan Harian', weight: 20 },
            { name: 'Tugas', weight: 30 },
            { name: 'Ujian Tengah Semester', weight: 25 },
            { name: 'Ujian Akhir Semester', weight: 25 },
        ];

        // Setup listeners and initialize data
        const setupListener = (collectionName, setData, initialData = null) => {
            const collectionRef = getCollectionRef(collectionName);
            if (!collectionRef) return null;

            if (initialData) {
                initializeData(collectionRef, initialData);
            }

            const q = query(collectionRef, orderBy("createdAt"));
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const dataList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setData(dataList);
            }, (error) => { });
            return unsubscribe;
        };

        // 🔧 PERBAIKAN: Setup listener untuk siswa dengan filter berdasarkan sekolah guru
        const setupStudentsListener = () => {
            try {
                if (!user || !user.namaSekolah) {
                    setStudents([]);
                    return;
                }

                // Ambil siswa dari sekolah yang sama dengan guru yang login
                getStudentsBySchool(user.namaSekolah).then(async (schoolStudents) => {
                    // Filter: hanya siswa yang sudah diimpor oleh guru ini
                    const importedStudents = schoolStudents.filter(student =>
                        student.importedBy === user.uid
                    );

                    // Hapus duplikasi berdasarkan ID siswa
                    const uniqueStudents = importedStudents.filter((student, index, self) =>
                        index === self.findIndex(s => s.id === student.id)
                    );

                    setStudents(uniqueStudents);
                }).catch(error => {
                    setStudents([]);
                });
            } catch (error) {
                setStudents([]);
            }
        };

        // 🔧 PERBAIKAN: Setup students listener dengan cleanup yang proper
        let studentsListenerCleanup = null;
        try {
            studentsListenerCleanup = setupStudentsListener();
        } catch (error) {
            // Error setting up students listener
        }

        // Setup periodic cleanup untuk data siswa yang tidak diperlukan
        const setupPeriodicCleanup = () => {
            const cleanupInterval = setInterval(async () => {
                try {
                    if (!user || !user.namaSekolah) return;

                    // Cleanup logic dapat ditambahkan di sini jika diperlukan
                } catch (error) {
                    // Error dalam periodic cleanup
                }
            }, 5 * 60 * 1000); // Jalankan setiap 5 menit

            return cleanupInterval;
        };



        const cleanupInterval = setupPeriodicCleanup();



        // Setup students listener
        const unsubAssessmentTypes = setupListener('assessmentTypes', setAssessmentTypes, initialAssessmentTypes);

        let unsubCptp = null;
        try {
            const cptpBaseRef = collection(db, `artifacts/${appId}/cptp_management`);
            try {
                const q = query(cptpBaseRef, where('createdBy', '==', user.uid));
                unsubCptp = onSnapshot(q, (snapshot) => {
                    const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    setCptps(list);
                }, () => setCptps([]));
            } catch (_) {
                getDocs(cptpBaseRef).then((snap) => {
                    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    setCptps(all.filter(x => x.createdBy === user.uid));
                }).catch(() => setCptps([]));
            }
        } catch (_) {
            setCptps([]);
        }

        const progressRef = getCollectionRef('progress');
        let unsubProgress = null;
        if (progressRef) {
            unsubProgress = onSnapshot(progressRef, (snapshot) => {
                const loadedProgress = {};
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.studentId && data.competencyId) {
                        loadedProgress[`${data.studentId}_${data.competencyId}`] = data.status;
                    }
                });
                setProgress(loadedProgress);
            }, (error) => { });
        }

        return () => {
            if (unsubAssessmentTypes && typeof unsubAssessmentTypes === 'function') {
                unsubAssessmentTypes();
            }
            if (unsubProgress && typeof unsubProgress === 'function') {
                unsubProgress();
            }
            if (unsubCptp && typeof unsubCptp === 'function') {
                unsubCptp();
            }
            // 🔧 PERBAIKAN: Cleanup students listener jika ada
            if (studentsListenerCleanup && typeof studentsListenerCleanup === 'function') {
                studentsListenerCleanup();
            }
            // 🔧 PERBAIKAN: Cleanup periodic cleanup interval
            if (cleanupInterval) {
                clearInterval(cleanupInterval);
            }

        };
    }, [user]);

    // --- Data Modification Functions ---
    const addDocToFirestore = async (collectionName, data) => {
        const collectionRef = getCollectionRef(collectionName);
        if (!collectionRef) return;
        await addDoc(collectionRef, { ...data, createdAt: serverTimestamp() });
    };



    const updateDocInFirestore = async (collectionName, docId, data) => {
        const collectionRef = getCollectionRef(collectionName);
        if (!collectionRef) return;
        await updateDoc(doc(collectionRef, docId), data);
    };

    const deleteDocFromFirestore = async (collectionName, docId) => {
        const collectionRef = getCollectionRef(collectionName);
        if (!collectionRef) return;
        await deleteDoc(doc(collectionRef, docId));
    };

    // --- Special functions for students (global collection) ---
    const addStudent = async (studentData) => {
        try {
            // 🔧 PERBAIKAN: Pastikan koleksi sekolah ada sebelum menambahkan siswa
            if (studentData.namaSekolah) {
                await ensureSchoolCollectionExists(studentData.namaSekolah);
            }

            // Gunakan koleksi yang terpisah berdasarkan sekolah
            const studentsRef = getSiswaCollectionRefBySchoolName(studentData.namaSekolah);
            await addDoc(studentsRef, { ...studentData, createdAt: serverTimestamp() });

        } catch (error) {
            throw error;
        }
    };

    const updateStudent = async (studentId, updates) => {
        // 🔧 PERBAIKAN: Gunakan koleksi yang terpisah berdasarkan sekolah
        const studentsRef = getSiswaCollectionRefBySchoolName(updates.namaSekolah);
        const studentRef = doc(studentsRef, studentId);
        await updateDoc(studentRef, updates);
    };

    const deleteStudent = async (studentId, namaSekolah) => {
        // 🔧 PERBAIKAN: Gunakan koleksi yang terpisah berdasarkan sekolah
        const studentsRef = getSiswaCollectionRefBySchoolName(namaSekolah);
        const studentRef = doc(studentsRef, studentId);
        await deleteDoc(studentRef);
    };

    // 🔧 PERBAIKAN: Fungsi untuk mendapatkan siswa berdasarkan sekolah
    const getStudentsBySchool = async (namaSekolah) => {
        try {
            const studentsRef = getSiswaCollectionRefBySchoolName(namaSekolah);

            const snapshot = await getDocs(studentsRef);
            const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Found students in collection

            // 🔧 PERBAIKAN: Hapus duplikasi berdasarkan ID
            const uniqueStudents = students.filter((student, index, self) =>
                index === self.findIndex(s => s.id === student.id)
            );

            console.log(`🔍 After removing duplicates: ${uniqueStudents.length} unique students`);

            return uniqueStudents;
        } catch (error) {
            console.error('❌ Error in getStudentsBySchool:', error);
            return [];
        }
    };

    // 🔧 PERBAIKAN: Fungsi untuk mendapatkan semua siswa dari semua sekolah
    const getAllStudentsFromAllSchools = async () => {
        try {
            // Ambil daftar sekolah
            const schoolsRef = collection(db, `artifacts/${appId}/schools`);
            const schoolsSnapshot = await getDocs(schoolsRef);

            let allStudents = [];

            // Ambil siswa dari setiap sekolah
            for (const schoolDoc of schoolsSnapshot.docs) {
                const schoolData = schoolDoc.data();
                const studentsRef = collection(db, `artifacts/${appId}/schools/${schoolDoc.id}/siswa`);
                const studentsSnapshot = await getDocs(studentsRef);

                const schoolStudents = studentsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    schoolId: schoolDoc.id,
                    namaSekolah: schoolData.namaSekolah || schoolData.name || 'Sekolah Tidak Diketahui'
                }));

                allStudents = [...allStudents, ...schoolStudents];
            }

            // Tambahkan siswa dari koleksi global (untuk kompatibilitas)
            const globalStudentsRef = collection(db, `artifacts/${appId}/siswa`);
            const globalSnapshot = await getDocs(globalStudentsRef);
            const globalStudents = globalSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                schoolId: 'global',
                namaSekolah: doc.data().namaSekolah || 'Sekolah Tidak Diketahui'
            }));

            return [...allStudents, ...globalStudents];
        } catch (error) {
            console.error('Error getting all students from all schools:', error);
            return [];
        }
    };





    // 🔧 PERBAIKAN: Fungsi untuk memindahkan data siswa dari koleksi global ke koleksi terpisah
    const migrateStudentsToSeparateCollections = async () => {
        try {
            console.log('🚀 Memulai migrasi data siswa ke koleksi terpisah...');

            // Ambil semua siswa dari koleksi global
            const globalStudentsRef = collection(db, `artifacts/${appId}/siswa`);
            const globalSnapshot = await getDocs(globalStudentsRef);

            if (globalSnapshot.empty) {
                console.log('✅ Tidak ada data siswa untuk dimigrasi');
                return { success: true, message: 'Tidak ada data siswa untuk dimigrasi' };
            }

            console.log(`📊 Ditemukan ${globalSnapshot.docs.length} siswa untuk dimigrasi`);

            let migratedCount = 0;
            let failedCount = 0;
            const failedStudents = [];

            // Proses setiap siswa
            for (const studentDoc of globalSnapshot.docs) {
                try {
                    const studentData = studentDoc.data();
                    const namaSekolah = studentData.namaSekolah || 'Sekolah Tidak Diketahui';

                    // Buat path sekolah yang aman
                    const schoolPath = namaSekolah.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

                    // Pindahkan ke koleksi terpisah
                    const targetCollectionRef = collection(db, `artifacts/${appId}/schools/${schoolPath}/siswa`);
                    await setDoc(doc(targetCollectionRef, studentDoc.id), {
                        ...studentData,
                        migratedAt: serverTimestamp(),
                        originalCollection: 'global'
                    });

                    // Hapus dari koleksi global
                    await deleteDoc(doc(globalStudentsRef, studentDoc.id));

                    migratedCount++;
                    console.log(`✅ Berhasil memindahkan siswa: ${studentData.nama || studentData.name || 'Nama Tidak Diketahui'}`);

                } catch (error) {
                    console.error(`❌ Gagal memindahkan siswa ${studentDoc.id}:`, error);
                    failedCount++;
                    failedStudents.push({
                        id: studentDoc.id,
                        data: studentDoc.data(),
                        error: error.message
                    });
                }
            }

            console.log(`🎉 Migrasi selesai! Berhasil: ${migratedCount}, Gagal: ${failedCount}`);

            return {
                success: true,
                message: `Migrasi selesai! Berhasil: ${migratedCount}, Gagal: ${failedCount}`,
                migratedCount,
                failedCount,
                failedStudents
            };

        } catch (error) {
            console.error('❌ Error dalam migrasi:', error);
            return {
                success: false,
                message: `Error dalam migrasi: ${error.message}`,
                error: error.message
            };
        }
    };

    // 🔧 NEW: Fungsi untuk cleanup data historis yang tidak valid
    const cleanupHistoricalData = async () => {
        try {
            console.log('🧹 Memulai cleanup data historis...');
            console.log('🔍 Menggunakan query sederhana untuk menghindari composite index requirements');

            const results = {
                deletedStudents: 0,
                deletedRombels: 0,
                deletedUsers: 0,
                cleanedReferences: 0,
                totalCleaned: 0,
                processedCollections: []
            };

            const errors = [];
            const warnings = [];

            // 1. Cleanup siswa dengan data tidak valid
            try {
                console.log('🔍 Mencari siswa dengan data tidak valid...');

                // Cari siswa tanpa nama atau NISN
                const invalidStudentsRef = collection(db, `artifacts/${appId}/siswa`);
                const invalidStudentsSnapshot = await getDocs(invalidStudentsRef);

                for (const studentDoc of invalidStudentsSnapshot.docs) {
                    const studentData = studentDoc.data();
                    let shouldDelete = false;

                    // Kriteria untuk hapus: tidak ada nama dan NISN
                    if ((!studentData.nama && !studentData.name) && !studentData.nisn) {
                        shouldDelete = true;
                    }

                    // Kriteria tambahan: data korup (createdAt sangat lama dan tidak ada aktivitas)
                    if (studentData.createdAt && studentData.createdAt.toDate) {
                        const createdDate = studentData.createdAt.toDate();
                        const oneYearAgo = new Date();
                        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

                        if (createdDate < oneYearAgo && !studentData.lastActivity && (!studentData.nama && !studentData.name)) {
                            shouldDelete = true;
                        }
                    }

                    if (shouldDelete) {
                        await deleteDoc(doc(invalidStudentsRef, studentDoc.id));
                        results.deletedStudents++;
                        console.log(`✅ Menghapus siswa tidak valid: ${studentDoc.id}`);
                    }
                }

            } catch (error) {
                console.error('❌ Error cleanup siswa:', error);
                errors.push(`Siswa cleanup error: ${error.message}`);
            }

            // 2. Cleanup rombel dengan data tidak valid
            try {
                console.log('🔍 Mencari rombel dengan data tidak valid...');

                const invalidRombelsRef = collection(db, `artifacts/${appId}/rombels`);
                const invalidRombelsSnapshot = await getDocs(invalidRombelsRef);

                for (const rombelDoc of invalidRombelsSnapshot.docs) {
                    const rombelData = rombelDoc.data();
                    let shouldDelete = false;

                    // Kriteria untuk hapus: tidak ada nama dan guru ID
                    if (!rombelData.nama && !rombelData.name && !rombelData.guruId) {
                        shouldDelete = true;
                    }

                    // Kriteria tambahan: status deleted tapi masih ada
                    if (rombelData.status === 'deleted' || rombelData.isDeleted === true) {
                        shouldDelete = true;
                    }

                    // Rombel dengan siswaIds kosong atau undefined yang sudah lama
                    if ((!rombelData.siswaIds || rombelData.siswaIds.length === 0) &&
                        rombelData.createdAt && rombelData.createdAt.toDate) {
                        const createdDate = rombelData.createdAt.toDate();
                        const sixMonthsAgo = new Date();
                        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

                        if (createdDate < sixMonthsAgo) {
                            shouldDelete = true;
                        }
                    }

                    if (shouldDelete) {
                        await deleteDoc(doc(invalidRombelsRef, rombelDoc.id));
                        results.deletedRombels++;
                        console.log(`✅ Menghapus rombel tidak valid: ${rombelDoc.id}`);
                    }
                }

            } catch (error) {
                console.error('❌ Error cleanup rombel:', error);
                errors.push(`Rombel cleanup error: ${error.message}`);
            }

            // 3. Cleanup user accounts yang soft deleted atau disabled lama (simplified query)
            try {
                console.log('🔍 Mencari user dengan status deleted/disabled...');

                const usersRef = collection(db, `artifacts/${appId}/users`);
                // Query sederhana tanpa composite index
                const allUsersSnapshot = await getDocs(usersRef);

                for (const userDoc of allUsersSnapshot.docs) {
                    const userData = userDoc.data();

                    // Filter di client-side untuk user dengan status deleted
                    if (userData.status === 'deleted') {
                        // Hapus user yang sudah deleted lebih dari 30 hari
                        if (userData.deletedAt && userData.deletedAt.toDate) {
                            const deletedDate = userData.deletedAt.toDate();
                            const thirtyDaysAgo = new Date();
                            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

                            if (deletedDate < thirtyDaysAgo) {
                                await deleteDoc(doc(usersRef, userDoc.id));
                                results.deletedUsers++;
                                console.log(`✅ Menghapus user deleted: ${userDoc.id}`);
                            }
                        }
                    }

                    // Hapus user disabled yang sudah lama (lebih dari 90 hari)
                    if (userData.isDisabled && userData.disabledAt && userData.disabledAt.toDate) {
                        const disabledDate = userData.disabledAt.toDate();
                        const ninetyDaysAgo = new Date();
                        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

                        if (disabledDate < ninetyDaysAgo) {
                            await deleteDoc(doc(usersRef, userDoc.id));
                            results.deletedUsers++;
                            console.log(`✅ Menghapus user disabled lama: ${userDoc.id}`);
                        }
                    }
                }

            } catch (error) {
                console.error('❌ Error cleanup users:', error);
                errors.push(`Users cleanup error: ${error.message}`);
            }

            // 4. Clean orphaned soal_ulangan (simplified to avoid complex index requirements)
            try {
                console.log('🔍 Membersihkan soal orphaned...');

                // Cari semua user guru yang masih aktif (query sederhana)
                const activeTeachersRef = collection(db, `artifacts/${appId}/users`);
                const teachersQuery = query(activeTeachersRef, where('role', '==', 'teacher'));
                const teachersSnapshot = await getDocs(teachersQuery);

                // Filter di client-side untuk menghindari composite index
                const activeTeacherIds = new Set();
                teachersSnapshot.docs.forEach(doc => {
                    const userData = doc.data();
                    // Filter di client: hanya ambil yang status bukan 'deleted'
                    if (userData.status !== 'deleted' && !userData.isDisabled) {
                        activeTeacherIds.add(doc.id);
                    }
                });

                console.log(`📊 Ditemukan ${activeTeacherIds.size} guru aktif untuk dicek soal-nya`);

                // Cari soal yang guru-nya sudah tidak aktif
                for (const teacherId of activeTeacherIds) {
                    try {
                        const soalRef = collection(db, `artifacts/${teacherId}/soal_ulangan`);
                        const soalSnapshot = await getDocs(soalRef);

                        for (const soalDoc of soalSnapshot.docs) {
                            const soalData = soalDoc.data();

                            // Hapus soal yang lebih dari 1 tahun dan tidak pernah digunakan
                            if (soalData.createdAt && soalData.createdAt.toDate) {
                                const createdDate = soalData.createdAt.toDate();
                                const oneYearAgo = new Date();
                                oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

                                // Kriteria cleanup yang lebih ketat untuk menghindari hapus data penting
                                if (createdDate < oneYearAgo &&
                                    !soalData.lastAccessed &&
                                    soalData.status !== 'active' &&
                                    soalData.status !== 'terkirim' &&
                                    (!soalData.deadline || new Date(soalData.deadline) < oneYearAgo)) {
                                    await deleteDoc(doc(soalRef, soalDoc.id));
                                    results.cleanedReferences++;
                                    console.log(`✅ Menghapus soal lama: ${soalDoc.id}`);
                                }
                            }
                        }
                    } catch (error) {
                        // Skip jika collection tidak ada
                        console.log(`⚠️ Collection soal_ulangan tidak ditemukan untuk teacher ${teacherId}`);
                    }
                }

            } catch (error) {
                console.error('❌ Error cleanup soal:', error);
                errors.push(`Soal cleanup error: ${error.message}`);
            }

            results.totalCleaned = results.deletedStudents + results.deletedRombels + results.deletedUsers + results.cleanedReferences;

            console.log('🎉 Cleanup selesai!', results);
            console.log('⚠️ Warnings (jika ada):', warnings);

            const successMessage = `Cleanup berhasil! Total dibersihkan: ${results.totalCleaned} item
            - Siswa: ${results.deletedStudents}
            - Rombel: ${results.deletedRombels}
            - Users: ${results.deletedUsers}
            - Referensi: ${results.cleanedReferences}`;

            return {
                success: true,
                message: successMessage,
                details: results,
                errors: errors.length > 0 ? errors : null,
                warnings: warnings.length > 0 ? warnings : null,
                note: 'Cleanup dilakukan dengan query sederhana untuk menghindari index requirements.'
            };

        } catch (error) {
            console.error('❌ Error dalam cleanup data historis:', error);
            return {
                success: false,
                message: `Error dalam cleanup: ${error.message}`,
                error: error.message
            };
        }
    };

    // 🔧 PERBAIKAN: Fungsi untuk mendapatkan daftar rombel yang tersedia
    const getAvailableRombels = async () => {
        try {
            if (!user || !user.namaSekolah) {
                console.log('⚠️ User atau nama sekolah tidak tersedia');
                return [];
            }

            console.log('🔍 Mencari rombel untuk sekolah:', user.namaSekolah);

            // Ambil rombel dari sekolah guru
            const rombelsRef = collection(db, `artifacts/${appId}/rombels`);
            const rombelsSnapshot = await getDocs(rombelsRef);

            const availableRombels = [];

            for (const rombelDoc of rombelsSnapshot.docs) {
                const rombelData = rombelDoc.data();
                console.log('📋 Rombel data:', rombelData);

                // Hanya rombel dari sekolah yang sama
                if (rombelData.namaSekolah === user.namaSekolah) {
                    console.log('✅ Rombel cocok dengan sekolah guru:', rombelData.nama || rombelData.name);

                    // Hitung siswa yang bergabung dengan multiple approach
                    let jumlahSiswa = 0;

                    try {
                        // Approach 1: Coba hitung dari field members rombel
                        if (rombelData.members && Array.isArray(rombelData.members)) {
                            jumlahSiswa = rombelData.members.length;
                            console.log(`👥 Jumlah siswa dari field members: ${jumlahSiswa}`);
                        }

                        // Approach 2: Coba hitung dari field siswaIds
                        if (jumlahSiswa === 0 && rombelData.siswaIds && Array.isArray(rombelData.siswaIds)) {
                            jumlahSiswa = rombelData.siswaIds.length;
                            console.log(`👥 Jumlah siswa dari field siswaIds: ${jumlahSiswa}`);
                        }

                        // Approach 3: Coba hitung dari field studentIds
                        if (jumlahSiswa === 0 && rombelData.studentIds && Array.isArray(rombelData.studentIds)) {
                            jumlahSiswa = rombelData.studentIds.length;
                            console.log(`👥 Jumlah siswa dari field studentIds: ${jumlahSiswa}`);
                        }

                        // Approach 4: Coba hitung dari subcollection siswa
                        if (jumlahSiswa === 0) {
                            try {
                                const rombelSiswaRef = collection(db, `artifacts/${appId}/rombels/${rombelDoc.id}/siswa`);
                                const rombelSiswaSnapshot = await getDocs(rombelSiswaRef);
                                jumlahSiswa = rombelSiswaSnapshot.size;
                                console.log(`👥 Jumlah siswa dari subcollection: ${jumlahSiswa}`);
                            } catch (error) {
                                console.warn('⚠️ Subcollection tidak tersedia:', error);
                            }
                        }

                        // Skip additional fallback checks since rombel functionality is removed

                    } catch (error) {
                        console.error(`❌ Error menghitung siswa di rombel ${rombelData.nama || rombelData.name}:`, error);
                        jumlahSiswa = 0;
                    }

                    availableRombels.push({
                        id: rombelDoc.id,
                        nama: rombelData.nama || rombelData.name || 'Rombel Tanpa Nama',
                        jumlahSiswa: jumlahSiswa,
                        namaSekolah: rombelData.namaSekolah,
                        createdAt: rombelData.createdAt
                    });
                } else {
                    console.log('❌ Rombel tidak cocok:', rombelData.namaSekolah, 'vs', user.namaSekolah);
                }
            }

            console.log('📊 Total rombel tersedia:', availableRombels.length);
            console.log('📋 Daftar rombel:', availableRombels);

            return availableRombels.sort((a, b) => b.createdAt?.toDate() - a.createdAt?.toDate());

        } catch (error) {
            console.error('❌ Error getting available rombels:', error);
            return [];
        }
    };

    // 🔧 PERBAIKAN: Fungsi untuk membuat koleksi sekolah jika belum ada
    const ensureSchoolCollectionExists = async (namaSekolah, retryCount = 0) => {
        if (!namaSekolah) return null;

        try {
            const schoolPath = namaSekolah.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
            const schoolRef = doc(db, `artifacts/${appId}/schools/${schoolPath}`);

            // Cek apakah dokumen sekolah sudah ada
            const schoolDoc = await getDoc(schoolRef);

            if (!schoolDoc.exists()) {
                // Buat dokumen sekolah baru
                await setDoc(schoolRef, {
                    namaSekolah: namaSekolah,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                console.log(`✅ Koleksi sekolah baru dibuat: ${namaSekolah}`);
            }

            return schoolPath;
        } catch (error) {
            console.error('❌ Error membuat koleksi sekolah:', error);

            // 🔧 PERBAIKAN: Handle offline error dengan retry mechanism
            if ((error.code === 'unavailable' || error.message?.includes('offline') || !navigator.onLine) && retryCount < 3) {
                console.log(`🔄 Retry attempt ${retryCount + 1}/3 for offline error...`);

                const delay = Math.pow(2, retryCount) * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));

                return ensureSchoolCollectionExists(namaSekolah, retryCount + 1);
            }

            return null;
        }
    };

    // 🔧 PERBAIKAN: Fungsi helper untuk menangani operasi database dengan retry
    const executeWithRetry = async (operation, maxRetries = 3, operationName = 'Database operation') => {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await operation();
            } catch (error) {
                lastError = error;
                console.error(`❌ ${operationName} attempt ${attempt} failed:`, error);

                // Check if it's an offline error
                if (error.code === 'unavailable' || error.message?.includes('offline') || !navigator.onLine) {
                    if (attempt < maxRetries) {
                        const delay = Math.pow(2, attempt - 1) * 1000;
                        console.log(`🔄 Retrying ${operationName} in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
                        await new Promise(resolve => setTimeout(resolve, delay));
                        continue;
                    }
                }

                // For non-offline errors, don't retry
                break;
            }
        }

        throw lastError;
    };

    // 🔧 PERBAIKAN: Fungsi untuk mengecek status koneksi
    const checkConnectionStatus = () => {
        const isOnline = navigator.onLine;
        console.log(`🌐 Connection status: ${isOnline ? 'Online' : 'Offline'}`);
        return isOnline;
    };

    // 🔧 PERBAIKAN: Fungsi untuk menunggu koneksi tersedia
    const waitForConnection = (timeout = 30000) => {
        return new Promise((resolve, reject) => {
            if (navigator.onLine) {
                resolve(true);
                return;
            }

            const timeoutId = setTimeout(() => {
                reject(new Error('Timeout waiting for connection'));
            }, timeout);

            const handleOnline = () => {
                clearTimeout(timeoutId);
                window.removeEventListener('online', handleOnline);
                resolve(true);
            };

            window.addEventListener('online', handleOnline);
        });
    };

    // Student Functions - using global collection
    // (addStudent, updateStudent, deleteStudent are defined above)


    // Assessment Type Functions
    const addAssessmentType = (assessmentData) => addDocToFirestore('assessmentTypes', { ...assessmentData, weight: Number(assessmentData.weight) });
    const updateAssessmentType = (assessmentId, updatedData) => updateDocInFirestore('assessmentTypes', assessmentId, { ...updatedData, weight: Number(updatedData.weight) });
    const deleteAssessmentType = (assessmentId) => deleteDocFromFirestore('assessmentTypes', assessmentId);

    // Progress Function (remains local for now, can be moved to Firestore later)
    const updateProgress = (studentId, competencyId, newStatus) => {
        setProgress(prev => ({ ...prev, [`${studentId}_${competencyId}`]: newStatus }));
    };

    const saveProgress = async () => {
        const progressCollectionRef = getCollectionRef('progress');
        if (!progressCollectionRef) throw new Error("User not authenticated.");

        const batch = writeBatch(db);

        for (const key in progress) {
            const [studentId, competencyId] = key.split('_');
            if (studentId && competencyId) {
                const docRef = doc(progressCollectionRef, key); // Use key as doc ID
                batch.set(docRef, {
                    studentId: studentId,
                    competencyId: competencyId,
                    status: progress[key],
                    updatedAt: serverTimestamp()
                });
            }
        }
        await batch.commit();
    };

    const value = {
        students,
        assessmentTypes,
        progress,
        cptps,
        addStudent,
        updateStudent,
        deleteStudent,
        getStudentsBySchool,
        getAllStudentsFromAllSchools,
        migrateStudentsToSeparateCollections,
        cleanupHistoricalData,
        ensureSchoolCollectionExists,

        getAvailableRombels,
        executeWithRetry,
        checkConnectionStatus,
        waitForConnection,
        addAssessmentType,
        updateAssessmentType,
        deleteAssessmentType,
        updateProgress,
        saveProgress,


        setStudents
    };

    return (
        <SiswaContext.Provider value={value}>
            {children}
        </SiswaContext.Provider>
    );
};

export const useSiswa = () => useContext(SiswaContext);
